import NiceModal, { register, useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { DummyComponent } from "../MuiHelpers";
import { ModalConfirmCloseReasons, modalConfirmDefaultComponents, ModalConfirmProps } from "./ModalConfirm";
import * as Styled from "./ModalConfirmWithHeader.styled";
import { Modals } from "./modals";
import { ModalTitleComponent } from "./ModalTitleComponent";

export const ModalConfirmWithHeader = NiceModal.create<ModalConfirmProps>(
    ({
        title,
        content,
        buttonText,
        width = 480,
        onCloseOverride,
        components = {
            ...modalConfirmDefaultComponents,
            DialogActionsWrapperComponent: null,
            DialogContentWrapperComponent: ({ children }) => <>{children}</>,
            ModalTitleComponent: ModalTitleComponent,
            PaperComponent: (props) => <Styled.ModalPaper {...props} $width={width} />,
        },
    }) => {
        const modal = useModal();
        const { t } = useTypedTranslation();

        const {
            DialogComponent,
            PaperComponent,
            ModalTitleComponent,
            DialogContentWrapperComponent,
            DialogActionsWrapperComponent,
            DialogActionButtonComponent,
        } = {
            ...modalConfirmDefaultComponents,
            ...components,
        };

        const handleClose = useCallback(
            (reason: ModalConfirmCloseReasons, result: boolean) => {
                let defaultPrevented = false;

                onCloseOverride?.({
                    reason: reason,
                    modal,
                    preventDefault: () => {
                        defaultPrevented = true;
                    },
                });

                if (!defaultPrevented) {
                    modal.resolve(result);
                    modal.hide();
                }
            },
            [modal, onCloseOverride],
        );

        const handleButton = useCallback(() => {
            handleClose("buttonClick", true);
        }, [handleClose]);

        const ContentComponent = content;

        return (
            <DialogComponent
                // we have to use a dummy component here because MUI Dialog requires a PaperComponent prop,
                // if notprovided it use Paper and it has a global style (see theme.js) that we don't want to use
                PaperComponent={DummyComponent}
                open={modal.visible}
                onClose={(_, reason) => handleClose(reason, false)}
            >
                <PaperComponent width={width}>
                    <ModalTitleComponent strong handleClose={() => handleClose("buttonClick", false)}>
                        {title}
                    </ModalTitleComponent>
                    <DialogContentWrapperComponent>
                        <ContentComponent parentModal={modal} />
                    </DialogContentWrapperComponent>
                    {!!DialogActionsWrapperComponent && (
                        <DialogActionsWrapperComponent>
                            <DialogActionButtonComponent component="button" onClick={handleButton}>
                                {buttonText ?? t("global.iUnderstand")}
                            </DialogActionButtonComponent>
                        </DialogActionsWrapperComponent>
                    )}
                </PaperComponent>
            </DialogComponent>
        );
    },
);

register(Modals.ModalConfirmWithHeader, ModalConfirmWithHeader);
